import React, { useState, useEffect } from 'react';
import { 
  Briefcase, Eye, Search, MapPin, Building2, 
  DollarSign, Users, Plus, Calendar, Send, Edit, ChevronDown, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import JobDetailsSubmitModal from '../JobDetailsSubmitModal';
import JobDetailModal from './JobDetailModal';
import SubJobsDisplay from '../SubJobsDisplay';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ViewToggle from '../shared/ViewToggle';
import StatusBadge from '../shared/StatusBadge';
import PriorityBadge from '../shared/PriorityBadge';
import AgeDisplay from '../shared/AgeDisplay';
import Pagination from '../shared/Pagination';
import JobCreationModal from '../JobCreationModal';
import TeamInfoBanner from './TeamInfoBanner';
import MultiSelectFilter from '../shared/MultiSelectFilter';
import EnhancedClientFilter from '../shared/EnhancedClientFilter';
import { formatSalaryRange } from '../../utils/formatters';
import { jobStatusGroups, generateClientGroups } from '../../utils/filterConfigs';
import { toast } from 'sonner';
import { isAccountManagement, isTeamLead, canViewAllClients } from '../../utils/roleUtils';
import LoadingOverlay, { LoadingDots } from '../LoadingOverlay';

/**
 * ============================================================================
 * FUNCTION: getClientAccess - Determine which clients a user can see
 * ============================================================================
 * PURPOSE: Calculate client visibility based on user role and team hierarchy
 * 
 * BUSINESS RULES (Single Source of Truth):
 * - Admin/JunoAdmin: See ALL clients
 * - Account Manager: See their own assigned_clients
 * - Lead Recruiter: See their own assigned_clients
 * - Regular Recruiter: Inherit team lead's assigned_clients
 * 
 * CRITICAL: Regular recruiters use TEAM LEAD's assigned_clients, NOT team entity
 * See: /app/CLIENT_VISIBILITY_BUSINESS_LOGIC.md
 * See: /app/METRICS_DEFINITIONS.md#client-visibility-rules
 * ============================================================================
 */
const getClientAccess = (selectedUser, myTeam, teamLead, allClients) => {
  const userRole = selectedUser?.role || selectedUser?.roles?.[0];
  
  // Admin roles - see everything
  if (userRole === 'admin' || userRole === 'junoadmin') {
    return {
      assignedClients: allClients,
      allClients: allClients,
      accessType: 'admin'
    };
  }
  
  // Supervisor roles - see all supervised teams' clients
  if (userRole === 'recruiter_supervisor') {
    // TODO: Implement supervised teams logic when supervisor data is available
    // For now, supervisors see all clients
    return {
      assignedClients: allClients,
      allClients: allClients,
      accessType: 'supervisor'
    };
  }
  
  // Account Managers: Use their own assigned_clients
  if (isAccountManagement(selectedUser)) {
    const myClients = selectedUser.assigned_clients || [];
    return {
      assignedClients: myClients,
      allClients: allClients,
      accessType: 'account_manager'
    };
  }
  
  // Lead Recruiters: Use their own assigned_clients
  if (isTeamLead(selectedUser) || userRole === 'lead_recruiter') {
    const myClients = selectedUser.assigned_clients || [];
    return {
      assignedClients: myClients,
      allClients: allClients,
      accessType: 'team_lead'
    };
  }
  
  // Regular Recruiters: Use TEAM LEAD's assigned_clients (inherited)
  // CRITICAL: This is the single source of truth for regular recruiter visibility
  if (userRole === 'recruiter') {
    const teamLeadClients = teamLead?.assigned_clients || [];
    return {
      assignedClients: teamLeadClients,
      allClients: allClients,
      accessType: 'team_member'
    };
  }
  
  // Default - no client access
  return {
    assignedClients: [],
    allClients: allClients,
    accessType: 'none'
  };
};

const JobsTableView = ({ selectedUser, jobs, submissions = [], myTeam, teamLead, initialFilters, onViewJob, onJobCreated, onRefreshSubmissions }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitModalJob, setSubmitModalJob] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetailModal, setShowJobDetailModal] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState(new Set()); // Track expanded parent jobs
  
  // ViewToggle state for My Jobs / All Jobs
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  
  const navigate = useNavigate();
  
  // Toggle expansion of parent jobs
  const toggleJobExpansion = (jobId) => {
    setExpandedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };
  
  // Check if a job belongs to the current user
  // Check if user can see this job in "My Jobs" filter
  // Shows ALL jobs from user's authorized companies (team-wide view)
  const isMyJob = React.useCallback((job) => {
    if (!selectedUser?.id || !job) return false;
    
    // Admin sees all jobs
    if (selectedUser.role === 'admin' || selectedUser.role === 'junoadmin') {
      return true;
    }
    
    // For regular recruiters: Show ALL jobs from team's authorized companies
    // This gives a team-wide view of all work for "My Companies"
    const jobCompany = job.client || job.client_company || '';
    
    if (teamLead && teamLead.assigned_clients) {
      const isAuthorizedCompany = teamLead.assigned_clients.some(assignedClient => {
        const clientWords = assignedClient.split(/\s+/);
        const firstWord = clientWords[0];
        return jobCompany.startsWith(firstWord) || jobCompany === assignedClient;
      });
      
      return isAuthorizedCompany;
    }
    
    return false;
  }, [selectedUser?.id, selectedUser?.role, teamLead]);

  // Check if user can EDIT a job (stricter - requires BOTH company auth AND assignment)
  const canEditJob = React.useCallback((job) => {
    if (!selectedUser?.id || !job) return false;
    
    // Admin/JunoAdmin can edit all jobs
    if (selectedUser.role === 'admin' || selectedUser.role === 'junoadmin') {
      return true;
    }
    
    // CRITICAL: Job's company MUST be in team's assigned clients
    const jobCompany = job.client || job.client_company || '';
    let isAuthorizedCompany = false;
    
    if (teamLead && teamLead.assigned_clients) {
      isAuthorizedCompany = teamLead.assigned_clients.some(assignedClient => {
        const clientWords = assignedClient.split(/\s+/);
        const firstWord = clientWords[0];
        return jobCompany.startsWith(firstWord) || jobCompany === assignedClient;
      });
    }
    
    // If company is not authorized, user cannot edit
    if (!isAuthorizedCompany) {
      return false;
    }
    
    // Company is authorized, now check if user has direct assignment
    if (job.created_by === selectedUser.id) {
      return true;
    }
    
    if (job.primary_recruiter_id === selectedUser.id || job.recruiter_id === selectedUser.id) {
      return true;
    }
    
    if (job.assigned_recruiters && Array.isArray(job.assigned_recruiters)) {
      if (job.assigned_recruiters.some(ar => ar.recruiter_id === selectedUser.id)) {
        return true;
      }
    }
    
    return false;
  }, [selectedUser?.id, selectedUser?.role, teamLead]);

  // Multi-select job status filter - Pre-select action-required statuses (matches Overview)
  const [selectedStatuses, setSelectedStatuses] = useState(new Set([
    'accepting_candidates',
    'interviewing'
  ]));
  
  // Enhanced client filtering state
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTimeframe, setSelectedTimeframe] = useState('all_time'); // Default to All Time to show all jobs
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Reset to page 1 when itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Time frame options for date filtering
  const timeframeOptions = [
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_12_months', label: 'Last 12 Months' },
    { value: 'all_time', label: 'All Time' },
    { value: 'ytd', label: 'YTD' }
  ];

  // Calculate date range based on timeframe
  const getDateRange = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    switch (selectedTimeframe) {
      case 'ytd':
        return { start: new Date(currentYear, 0, 1), end: now };
      case 'this_month':
        return { start: new Date(currentYear, now.getMonth(), 1), end: now };
      case 'last_month':
        const lastMonth = new Date(currentYear, now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(currentYear, now.getMonth(), 0);
        return { start: lastMonth, end: lastMonthEnd };
      case 'last_6_months':
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        return { start: sixMonthsAgo, end: now };
      case 'last_12_months':
        const twelveMonthsAgo = new Date(now);
        twelveMonthsAgo.setMonth(now.getMonth() - 12);
        return { start: twelveMonthsAgo, end: now };
      case 'all_time':
        return { start: new Date(2000, 0, 1), end: now };
      default:
        return { start: new Date(currentYear, 0, 1), end: now };
    }
  };

  // Get all unique clients from jobs
  const allClients = React.useMemo(() => {
    const clients = [...new Set(jobs.map(job => job.client || job.client_company).filter(Boolean))];
    return clients.sort();
  }, [jobs]);

  // Get client access based on user role and team lead's assigned clients
  const clientAccess = React.useMemo(() => {
    return getClientAccess(selectedUser, myTeam, teamLead, allClients);
  }, [selectedUser, myTeam, teamLead, allClients]);

  // Get unique clients for filter dropdown
  const clients = allClients;

  // ============================================================================
  // CRITICAL: Pre-populate client filter with user's assigned clients
  // ============================================================================
  // PURPOSE: Auto-select clients the recruiter has access to
  // BUSINESS RULE: Regular recruiters inherit team lead's assigned_clients
  // ============================================================================
  // Default behavior: Show ALL jobs (empty filter = show all)
  // Users can manually select specific clients if they want to filter
  useEffect(() => {
    console.log('[JobsTableView] Client filter defaulting to "Show All":', {
      accessType: clientAccess.accessType,
      assignedClients: clientAccess.assignedClients?.length || 0,
      userRole: selectedUser?.role
    });
    // Keep selectedClients empty by default to show all jobs
    // setSelectedClients remains at initial empty Set
  }, [clientAccess.assignedClients, clientAccess.accessType, selectedUser, teamLead]);

  // Calculate submission counts per job
  const submissionCounts = React.useMemo(() => {
    console.log('[JobsTableView] Calculating submission counts from', submissions.length, 'submissions');
    const counts = {};
    submissions.forEach(sub => {
      const jobId = sub.job_id;
      if (jobId) {
        counts[jobId] = (counts[jobId] || 0) + 1;
      }
    });
    const totalCounted = Object.keys(counts).length;
    const sample = Object.entries(counts).slice(0, 3);
    console.log('[JobsTableView] Submission counts:', {
      totalJobsWithSubmissions: totalCounted,
      sampleCounts: sample,
      totalSubmissions: Object.values(counts).reduce((a, b) => a + b, 0)
    });
    return counts;
  }, [submissions]);

  // Debug logging
  React.useEffect(() => {
    console.log('[JobsTableView] Filter state:', {
      totalJobs: jobs.length,
      selectedClients: Array.from(selectedClients),
      selectedStatuses: Array.from(selectedStatuses),
      selectedTimeframe
    });
  }, [jobs.length, selectedClients, selectedStatuses, selectedTimeframe]);

  // Show filtering indicator when filter state changes
  useEffect(() => {
    if (jobs.length > 0) {
      setIsFiltering(true);
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 300); // Show loading for 300ms during filtering
      return () => clearTimeout(timer);
    }
  }, [selectedClients, selectedStatuses, selectedTimeframe, searchTerm, jobs.length]);

  // Apply filters to jobs - Enhanced client filtering allows all clients but prioritizes assigned
  const filteredJobs = jobs.filter(job => {
    // FIRST: Exclude sub-jobs from main list (they'll be shown under parent jobs)
    if (job.parent_job_id) {
      return false;
    }
    
    const matchesSearch = searchTerm === '' || 
      (job.display_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.department || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.client || job.client_company || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Enhanced client filter - shows selected clients (can be assigned + additional)
    const jobClient = job.client || job.client_company || '';
    const matchesClient = selectedClients.size === 0 || selectedClients.has(jobClient);
    
    // Multi-select job status filter using Juno schema statuses (lowercase)
    const jobStatus = (job.status || '').toLowerCase();
    const matchesStatus = selectedStatuses.size === 0 || selectedStatuses.has(jobStatus);
    
    // Timeframe filter - based on date_added
    const dateRange = getDateRange();
    const jobDate = new Date(job.date_added || job.created_at || 0);
    const matchesTimeframe = selectedTimeframe === 'all_time' || (jobDate >= dateRange.start && jobDate <= dateRange.end);
    
    // ViewMode filter - My Jobs vs All Jobs
    const matchesOwnership = viewMode === 'all' || isMyJob(job);
    
    return matchesSearch && matchesClient && matchesStatus && matchesTimeframe && matchesOwnership;
  }).sort((a, b) => {
    // Sort by most recent first using date_added (PRIMARY) or created_at (FALLBACK)
    // Critical: date_added contains actual data, created_at is migration timestamp
    const dateA = new Date(a.date_added || a.created_at || 0);
    const dateB = new Date(b.date_added || b.created_at || 0);
    return dateB - dateA; // Descending order (newest first)
  })

  // Pagination
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedJobs = filteredJobs.slice(startIndex, startIndex + itemsPerPage);

  const handleJobSuccess = (newJob) => {
    toast.success('Job created successfully!');
    // Callback to parent to refresh jobs list
    onJobCreated && onJobCreated(newJob);
  };

  return (
    <div className="space-y-4" data-testid="jobs-table-view">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Briefcase className="h-6 w-6 text-orange-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Jobs ({filteredJobs.length})
              </h3>
              <p className="text-sm text-gray-600">
                {selectedStatuses.size} statuses, {selectedClients.size} clients
              </p>
            </div>
          </div>
          <Button
            onClick={() => setIsModalOpen(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="add-job-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add a Job
          </Button>
        </div>
      </div>

      {/* Job Creation Modal */}
      <JobCreationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleJobSuccess}
        selectedUser={selectedUser}
        teamLead={teamLead}
        onNavigateToCompanies={() => {
          if (window.onNavigateToCompaniesFromJob) {
            window.onNavigateToCompaniesFromJob();
          }
        }}
      />

      {/* Team Info Banner - Shows team, lead recruiter, and assigned clients */}
      <TeamInfoBanner 
        selectedUser={selectedUser}
        teamLead={teamLead}
        myTeam={myTeam}
      />

      {/* View Toggle - My Jobs / All Jobs */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {viewMode === 'my' ? 'My Jobs' : 'All Jobs'} ({filteredJobs.length})
          </h3>
          <ViewToggle 
            leftLabel="My Jobs"
            rightLabel="All Jobs"
            activeView={viewMode}
            onToggle={setViewMode}
            leftValue="my"
            rightValue="all"
          />
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <Search className="absolute ml-3 mt-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ID, title, department, or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Job Status Filter - Multi-Select */}
          <MultiSelectFilter
            label="Job Status"
            selectedItems={selectedStatuses}
            onToggle={(status) => {
              const newStatuses = new Set(selectedStatuses);
              if (newStatuses.has(status)) {
                newStatuses.delete(status);
              } else {
                newStatuses.add(status);
              }
              setSelectedStatuses(newStatuses);
            }}
            groups={jobStatusGroups}
            testId="job-status-filter"
          />

          {/* Enhanced Client Filter */}
          <EnhancedClientFilter
            assignedClients={clientAccess.assignedClients}
            allClients={clientAccess.allClients}
            selectedClients={selectedClients}
            onSelectionChange={setSelectedClients}
            accessType={clientAccess.accessType}
            userRole={selectedUser?.role || selectedUser?.roles?.[0]}
          />
        </div>

        {/* Timeframe Filter */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              Jobs Added: {timeframeOptions.find(opt => opt.value === selectedTimeframe)?.label}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {timeframeOptions.map(option => (
              <button
                key={option.value}
                onClick={() => {
                  setSelectedTimeframe(option.value);
                  setCurrentPage(1); // Reset to first page when filter changes
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTimeframe === option.value
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-300'
                }`}
                data-testid={`timeframe-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden relative">
        {/* Show loading indicator when filtering */}
        {isFiltering && (
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg shadow-sm">
            <LoadingDots className="scale-75" />
            <span className="text-xs font-medium text-blue-700">Filtering...</span>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Job Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Client
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Openings
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Submissions
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Date Added
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Job Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedJobs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Briefcase className="h-12 w-12 text-gray-400" />
                      <div>
                        <p className="text-gray-600 font-medium">No jobs found</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {searchTerm || selectedClients.size > 0 || selectedStatuses.size > 0
                            ? 'Try adjusting your filters'
                            : 'No jobs available'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedJobs.map((job, index) => {
                  return (
                    <React.Fragment key={job.id}>
                    <tr 
                      className="hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedJob(job);
                        setShowJobDetailModal(true);
                      }}
                      data-testid={`job-row-${index}`}
                    >
                      {/* Job ID */}
                      <td className="px-4 py-3">
                        {job.display_id ? (
                          <span className="inline-block px-2 py-1 text-xs font-mono font-semibold bg-blue-100 text-blue-800 rounded">
                            {job.display_id}
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-1 text-xs font-mono font-semibold bg-red-100 text-red-800 rounded">
                            ERROR
                          </span>
                        )}
                      </td>
                      
                      {/* Job Title */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {/* Expand/Collapse icon for parent jobs */}
                          {job.is_parent_job && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleJobExpansion(job.id);
                              }}
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              {expandedJobs.has(job.id) ? (
                                <ChevronDown className="h-4 w-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-600" />
                              )}
                            </button>
                          )}
                          <button className="text-blue-600 hover:text-blue-800 font-medium text-left">
                            {job.title || 'Untitled Position'}
                            {job.is_parent_job && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({job.no_of_openings} openings)
                              </span>
                            )}
                          </button>
                        </div>
                      </td>

                      {/* Client */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-gray-700">
                          <Building2 className="h-3 w-3 text-gray-400" />
                          {job.client || job.client_company || 'N/A'}
                        </div>
                      </td>

                      {/* Location */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="h-3 w-3" />
                          {job.location || 'Remote'}
                        </div>
                      </td>

                      {/* Openings */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-gray-700">
                          <Users className="h-3 w-3" />
                          {job.no_of_openings || job.openings || 1}
                        </div>
                      </td>

                      {/* Submissions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center">
                          <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full text-xs font-semibold ${
                            submissionCounts[job.id] > 0 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {submissionCounts[job.id] || 0}
                          </span>
                        </div>
                      </td>

                      {/* Date Added */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs text-gray-700">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          {job.date_added || job.created_at 
                            ? new Date(job.date_added || job.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'N/A'}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge status={job.status || 'Active'} />
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => setSubmitModalJob(job)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                            title="Submit Candidates"
                            data-testid="submit-candidate-btn"
                          >
                            <Send className="h-3.5 w-3.5" />
                            <span>Submit</span>
                          </button>
                          <button 
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View Details"
                            onClick={() => {
                              setSelectedJob(job);
                              setShowJobDetailModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Sub-jobs row (only for expanded parent jobs) */}
                    {job.is_parent_job && expandedJobs.has(job.id) && (
                      <tr className="bg-gray-50">
                        <td colSpan="9" className="px-4 py-3">
                          <SubJobsDisplay 
                            parentJob={job} 
                            onJobSelect={(subJob) => {
                              setSelectedJob(subJob);
                              setShowJobDetailModal(true);
                            }}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredJobs.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>
      
      {/* Submit Candidates Modal */}
      {submitModalJob && (
        <JobDetailsSubmitModal
          isOpen={true}
          onClose={() => setSubmitModalJob(null)}
          job={submitModalJob}
          onSubmitSuccess={() => {
            setSubmitModalJob(null);
            // Refresh submissions data to show new submission immediately
            if (onRefreshSubmissions) {
              onRefreshSubmissions();
            }
            toast.success('Candidates submitted successfully!');
          }}
        />
      )}

      {/* Edit Job Modal - Now using same modal as creation for consistency */}
      {showEditModal && editingJob && (
        <JobCreationModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingJob(null);
          }}
          editingJob={editingJob}
          selectedUser={selectedUser}
          teamLead={teamLead}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingJob(null);
            if (onJobCreated) {
              onJobCreated();
            }
            toast.success('Job updated successfully!');
          }}
        />
      )}

      {/* Job Detail Modal - Similar to Candidate Detail Modal */}
      <JobDetailModal
        job={selectedJob}
        isOpen={showJobDetailModal}
        onClose={() => {
          setShowJobDetailModal(false);
          setSelectedJob(null);
        }}
        submissions={submissions}
        canEdit={selectedJob ? canEditJob(selectedJob) : false}
        onEdit={(job) => {
          setShowJobDetailModal(false);
          setEditingJob(job);
          setShowEditModal(true);
        }}
      />
    </div>
  );
};

export default JobsTableView;
